import { enhancedApi as generatedApi } from './generated/policeInvestigationService';

/**
 * Enhanced version of generated police investigation API with:
 * - Better cache invalidation (matter-specific tags)
 * - Fixed types for reconcile endpoint (override Blob type)
 *
 * Note: Cross-invalidation with police_reports is handled by policeReportsService
 * which invalidates both tags when updating investigations.
 */
export const enhancedPoliceInvestigationApi = generatedApi
  .injectEndpoints({
    endpoints: (build) => ({
      // Override reconcile endpoint with proper typing (generated has Blob type)
      policeInvestigationReconcileMultiple: build.mutation<
        {
          processed: number;
          failed: number;
          results: Array<{ matter_id: string; status: string }>;
          errors: Array<{ matter_id: string; error: string }>;
        },
        {
          matterId?: string; // Optional since this can be called from global context
          body: {
            matter_ids: string[];
            action?: 'reconcile' | 'mark_completed' | 'close_no_report';
          };
        }
      >({
        query: ({ matterId, body }) => ({
          url: matterId
            ? `/api/matters/${matterId}/police-investigation/reconcile-multiple/`
            : '/api/police-investigation/reconcile-multiple/',
          method: 'POST',
          body,
        }),
        invalidatesTags: (result, error, { body }) => [
          'police_investigation',
          // Invalidate each specific matter
          ...body.matter_ids.map((id) => ({
            type: 'police_investigation' as const,
            id: `investigation-${id}`,
          })),
        ],
      }),
    }),
    overrideExisting: true,
  })
  .enhanceEndpoints({
    endpoints: {
      // Add matter-specific tags for better cache management
      policeInvestigationList: {
        providesTags: (result, error, arg) => [
          {
            type: 'police_investigation' as const,
            id: `investigation-${arg.matterId}`,
          },
          { type: 'police_investigation' as const, id: arg.matterId },
          'police_investigation',
        ],
      },
      policeInvestigationUpdate: {
        invalidatesTags: (result, error, arg) => [
          {
            type: 'police_investigation' as const,
            id: `investigation-${arg.matterId}`,
          },
          { type: 'police_investigation' as const, id: arg.matterId },
          'police_investigation',
        ],
      },
      policeInvestigationPartialUpdate: {
        invalidatesTags: (result, error, arg) => [
          {
            type: 'police_investigation' as const,
            id: `investigation-${arg.matterId}`,
          },
          { type: 'police_investigation' as const, id: arg.matterId },
          'police_investigation',
        ],
      },
    },
  });

// Export all hooks
export const {
  // Queries
  usePoliceInvestigationListQuery,
  // Mutations
  usePoliceInvestigationPartialUpdateMutation,
  usePoliceInvestigationReconcileMultipleMutation,
  usePoliceInvestigationUpdateMutation,
} = enhancedPoliceInvestigationApi;

// Re-export types from generated service for convenience
export type {
  MatterInvestigationStatus,
  PoliceInvestigationListApiArg,
  PoliceInvestigationPartialUpdateApiArg,
  PoliceInvestigationUpdateApiArg,
  ReadMatterInvestigation,
  UpdateMatterInvestigation,
} from './generated/policeInvestigationService';
